import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import type { AddressResponse } from '~/types/global'
import type { ApplicantHeadData } from '../../applicants/types'

type IntegrateApplicationPayload = {
  applicationId: number
  email: string
  firstname?: string
  lastname?: string
  phone?: string
  address?: Omit<AddressResponse, 'id'>
  qualifications?: Array<number | string>
  segmentations?: Array<number | string>
  skipInvalidation?: boolean
}

export const useIntegrateApplication = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ApplicantHeadData,
    ApiResponseError,
    IntegrateApplicationPayload
  >({
    mutationFn: async ({ applicationId, ...payload }) => {
      const requestPayload = {
        ...payload,
        qualifications: payload.qualifications?.map(
          (q) => `/v1/taxonomies/qualifications/${q}`
        ),
        segmentations: payload.segmentations?.map(
          (s) => `/v1/taxonomies/segmentations/${s}`
        )
      }

      return await fetchApiWithToken<ApplicantHeadData>({
        method: 'post',
        url: `/v1/applications/${applicationId}/integrate`,
        data: requestPayload
      })
    },
    onSuccess: (_, variables) => {
      if (variables.skipInvalidation) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: ['applications', variables.applicationId]
      })

      queryClient.invalidateQueries({
        queryKey: ['applications']
      })
    }
  })
}
